import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Badge, Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';

interface Response {
  text: string;
  tone: string;
}

interface Props {
  responses: Response[];
  onSelect: (response: Response, index: number) => void;
  onRegenerate: () => void;
  isLoading: boolean;
  aiSuggestedError: string | null;
}

export default function SuggestedResponsesSelector({
  responses,
  onSelect,
  onRegenerate,
  isLoading,
  aiSuggestedError,
}: Props) {
  const { t } = useTranslation('chat/chat');

  if (responses.length === 0 && !isLoading) {
    return (
      <div className="w-64 p-4 shadow-xl bg-base-100 rounded-box border border-base-300 text-center">
        <p className="text-sm text-base-content/70">{aiSuggestedError}</p>
        <Button
          onClick={onRegenerate}
          size="sm"
          color="ghost"
          className="mt-2 normal-case"
        >
          Reformular sugerencias
        </Button>
      </div>
    );
  }

  return (
    <div className="w-80 p-2 shadow-xl bg-base-100 rounded-box border border-base-300 overflow-hidden">
      <div className="flex flex-col gap-3 max-h-[400px] overflow-y-auto p-1">
        {responses.map((response, idx) => (
          <div
            key={`${response.tone}-${idx}`}
            className="flex flex-col gap-2 p-3 rounded-lg border border-base-200 hover:border-primary/50 transition-colors bg-base-200/50"
          >
            <div className="flex justify-between items-start gap-2">
              <Badge color="primary" size="sm" className="capitalize py-2 px-3">
                {response.tone}
              </Badge>
            </div>
            <p className="text-sm leading-relaxed text-base-content whitespace-pre-wrap">
              {response.text}
            </p>
            <Button
              size="xs"
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                onSelect(response, idx);
              }}
              className="mt-1 normal-case font-semibold"
            >
              {t('toolActions.suggestedTextBtnOption')}
            </Button>
          </div>
        ))}

        <Button
          onClick={(e) => {
            e.preventDefault();
            onRegenerate();
          }}
          disabled={isLoading}
          size="sm"
          color="ghost"
          className="mt-2 normal-case flex items-center justify-center gap-2 hover:bg-base-300"
        >
          {isLoading ? (
            <span className="loading loading-spinner loading-xs"></span>
          ) : (
            <ArrowPathIcon className="w-4 h-4" />
          )}
          {isLoading ? 'Generando...' : 'Reformular sugerencias'}
        </Button>
      </div>
    </div>
  );
}
